import React, { useEffect, useState } from 'react';
import { formatPhoneToE164, formatAllCapsLabel, formatE164ToPhone } from '../common/utils';
import { PaymentPlatform } from '../services/payment/types';
import { useHeader } from './';
import { useUserService } from '../services/user';

const PaymentSettingsModal = () => {
    const { isPaymentSettingsOpen, closePaymentSettings } = useHeader();
    const userService = useUserService();

    const [platform, setPlatform] = useState<PaymentPlatform>(PaymentPlatform.ZELLE);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [isLoadingPreferences, setIsLoadingPreferences] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const isBusy = isLoadingPreferences || isSubmitting;

    useEffect(() => {
        if (!isPaymentSettingsOpen) return;

        setIsLoadingPreferences(true);
        userService.getUserInfo()
            .then(details => {
                if(details){
                    setPlatform(details.paymentPlatform);
                    setPhoneNumber(formatE164ToPhone(details.phoneNumber));
                }
            })
            .catch(err => console.error('Failed to load payment preferences', err))
            .finally(() => setIsLoadingPreferences(false));
    }, [isPaymentSettingsOpen, userService]);

    const handleOverlay = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget && !isBusy) closePaymentSettings();
    };

    const handleConfirm = async () => {
        setError(null);

        const formattedPhone = formatPhoneToE164(phoneNumber);
        if (!formattedPhone) {
            setError('Enter a valid 10-digit phone number');
            return;
        }

        setIsSubmitting(true);
        try {
            await userService.updatePaymentDetails({
                paymentPlatform: platform,
                phoneNumber: formattedPhone,
            });
            closePaymentSettings();
        } catch (err) {
            console.error(err);
            setError('Something went wrong. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return isPaymentSettingsOpen ? (
        <div className="modal-overlay open" onClick={handleOverlay}>
            <div className="modal payment-settings-modal">
                <button className="modal-close" onClick={closePaymentSettings} disabled={isBusy}>✕</button>

                <div className="modal-title">Payment settings</div>

                { isLoadingPreferences ? (
                    <div className="modal-sub">Loading your payment details…</div>
                ) : (
                    <>
                        <div className="modal-sub">Where should we send your cash outs?</div>

                        <div className="field-group">
                            <label className="field-label">Payment platform</label>
                            <div className="field-select-wrap">
                                <select
                                    className="field-select"
                                    value={platform}
                                    onChange={e => setPlatform(e.target.value as PaymentPlatform)}
                                    disabled={isBusy}
                                >
                                    {Object.values(PaymentPlatform).map(p => (
                                        <option key={p} value={p}>{formatAllCapsLabel(p)}</option>
                                    ))}
                                </select>
                                <span className="field-select-arrow">▾</span>
                            </div>
                        </div>

                        <div className="field-group">
                            <label className="field-label">Phone number</label>
                            <input
                                type="tel"
                                className="field-input mono"
                                placeholder="(555) 123-4567"
                                value={phoneNumber}
                                onChange={e => setPhoneNumber(e.target.value)}
                                disabled={isBusy}
                            />
                        </div>

                        { error &&
                            <div className="field-error">{error}</div>
                        }

                        <div className="modal-cta">
                            <button className="btn-primary" onClick={handleConfirm} disabled={isBusy}>
                                { isSubmitting ? <span className="btn-spinner" /> : 'Save' }
                            </button>
                            <button className="btn-ghost" onClick={closePaymentSettings} disabled={isBusy}>Cancel</button>
                        </div>
                    </>
                )}
            </div>
        </div>
    ) : null;
};

export default PaymentSettingsModal;